import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  Users, User, UserCheck, Search, RefreshCw, Loader2, 
  ChevronDown, ChevronUp, Mail, Hotel, MessageSquare,
  Eye, AlertCircle, CreditCard, Filter, X
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { cn } from '../../../lib/utils';
import { API_URL } from '../../../utils/api';
import DancerPaymentShareManager from '../DancerPaymentShareManager';
import GuestListCSVExport from './GuestListCSVExport';

// ============== UTILITY FUNCTIONS ==============

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount || 0);
};

// ============== STAT CARD COMPONENT ==============

const StatCard = ({ title, value, subtitle, icon: Icon, variant = 'default' }) => {
  const variants = {
    default: 'text-slate-900',
    success: 'text-emerald-600',
    warning: 'text-orange-600',
    muted: 'text-slate-500',
  };
  
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-2 text-slate-500 mb-1">
        {Icon && <Icon className="w-4 h-4" />}
        <span className="text-sm">{title}</span>
      </div>
      <p className={cn("text-xl font-bold", variants[variant])}>{value}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
  );
};

// ============== PAYMENT SHARE STATUS BADGE ==============

const PaymentShareStatusBadge = ({ status, onClick }) => {
  if (!status || status === 'none') {
    return null;
  }

  const statusConfig = {
    active: { label: 'Active', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    paused: { label: 'Paused', className: 'bg-orange-100 text-orange-700 border-orange-200' },
    completed: { label: 'Paid', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    cancelled: { label: 'Cancelled', className: 'bg-slate-100 text-slate-500 border-slate-200' },
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <Badge 
      variant="outline" 
      className={cn("text-xs cursor-pointer hover:opacity-80", config.className)}
      onClick={onClick}
    >
      <CreditCard className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
};

// ============== ATTENDEE DETAIL SHEET ==============

const GuestDetailSheet = ({ guest, open, onClose, onViewBooking, onManageShare, authToken }) => {
  if (!guest) return null;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {guest.isBookingContact ? (
              <UserCheck className="w-5 h-5 text-violet-600" />
            ) : (
              <User className="w-5 h-5 text-slate-400" />
            )}
            {guest.name || 'Unknown Attendee'}
          </SheetTitle>
          <SheetDescription>
            {guest.isBookingContact ? 'Booking Contact & Attendee' : 'Attendee'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Attendee Info */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Attendee</h4>
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-900">{guest.name || 'Not provided'}</span>
              </div>
              {guest.email && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <a href={`mailto:${guest.email}`} className="text-sm text-violet-600 hover:underline">
                    {guest.email}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Booking Info */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Booking</h4>
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div>
                <span className="text-xs text-slate-500">Booking Reference</span>
                <p className="font-mono text-sm text-slate-900">{guest.bookingId}</p>
              </div>
              <div>
                <span className="text-xs text-slate-500">Booking Contact</span>
                <p className="text-sm font-medium text-slate-900">{guest.contactName}</p>
                {guest.isBookingContact && (
                  <span className="text-xs text-violet-600">(This attendee)</span>
                )}
              </div>
              <div>
                <span className="text-xs text-slate-500">Booking Status</span>
                <Badge className={cn(
                  "mt-1",
                  guest.bookingStatus === 'fully_paid' || guest.bookingStatus === 'confirmed' 
                    ? 'bg-emerald-100 text-emerald-700'
                    : guest.bookingStatus === 'deposit_paid'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-yellow-100 text-yellow-700'
                )}>
                  {guest.bookingStatus?.replace('_', ' ') || 'Pending'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Accommodation */}
          {guest.roomType && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Accommodation</h4>
              <div className="bg-slate-50 rounded-lg p-4 flex items-center gap-3">
                <Hotel className="w-5 h-5 text-slate-400" />
                <span className="text-sm text-slate-900">{guest.roomType}</span>
              </div>
            </div>
          )}

          {/* Add-ons */}
          {guest.addOns && guest.addOns.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Add-ons (Booking)</h4>
              <div className="space-y-2">
                {guest.addOns.map((addon, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-lg p-3 text-sm text-slate-900">
                    {addon}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Special Requests - Booking Level */}
          {guest.specialRequests && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Special Requests (Booking)</h4>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <MessageSquare className="w-4 h-4 text-orange-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-orange-600 mb-1">This request applies to the entire booking</p>
                    <p className="text-sm text-slate-700">{guest.specialRequests}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Share Status */}
          {guest.paymentShareStatus && guest.paymentShareStatus !== 'none' && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wide">Payment Share</h4>
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <PaymentShareStatusBadge status={guest.paymentShareStatus} />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onManageShare(guest.booking)}
                    className="text-violet-600 border-violet-200 hover:bg-violet-50"
                  >
                    <CreditCard className="w-4 h-4 mr-1" />
                    Manage Share
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="pt-4 border-t space-y-2">
            <Button
              variant="default"
              className="w-full bg-violet-600 hover:bg-violet-700"
              onClick={() => onViewBooking(guest.booking)}
            >
              <Eye className="w-4 h-4 mr-2" />
              View Full Booking
            </Button>
            {(!guest.paymentShareStatus || guest.paymentShareStatus === 'none') && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => onManageShare(guest.booking)}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Manage Payment Shares
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ============== PAYMENT SHARE MANAGER DIALOG ==============

const PaymentShareDialog = ({ booking, open, onClose, authToken, onUpdate }) => {
  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-violet-600" />
            Payment Shares - {booking.booking_id}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <DancerPaymentShareManager 
            booking={booking} 
            authToken={authToken} 
            onUpdate={onUpdate}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============== MAIN COMPONENT ==============

export default function WeekenderGuests({ 
  event, 
  bookings,
  loading, 
  authToken,
  onRefresh,
  onBookingSelect
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [guestFilter, setGuestFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [shareDialogBooking, setShareDialogBooking] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [paymentShares, setPaymentShares] = useState({});
  const [sharesLoading, setSharesLoading] = useState(false);

  // Active bookings (exclude cancelled/refunded)
  const activeBookings = useMemo(() => {
    return (bookings || []).filter(b => 
      b.status !== 'cancelled' && b.status !== 'refunded'
    );
  }, [bookings]);

  // Fetch payment shares for all bookings
  const fetchPaymentShares = useCallback(async () => {
    if (!activeBookings.length || !authToken) return;

    setSharesLoading(true);
    const sharesMap = {};
    
    try {
      // Fetch shares for each booking (could optimize with bulk endpoint if available)
      await Promise.all(activeBookings.map(async (booking) => {
        try {
          const response = await fetch(
            `${API_URL}/api/dancer-payments/admin/bookings/${booking.booking_id}/shares`,
            { headers: { 'Authorization': `Bearer ${authToken}` } }
          );
          if (response.ok) {
            const data = await response.json();
            sharesMap[booking.booking_id] = data.shares || [];
          }
        } catch (err) {
          console.error(`Failed to fetch shares for ${booking.booking_id}:`, err);
        }
      }));
      
      setPaymentShares(sharesMap);
    } catch (err) {
      console.error('Failed to fetch payment shares:', err);
    } finally {
      setSharesLoading(false);
    }
  }, [activeBookings, authToken]);

  // Fetch shares on mount
  useEffect(() => {
    fetchPaymentShares();
  }, [fetchPaymentShares]);

  // Build flat attendee list from bookings - SOURCE: guests[] array ONLY
  // customer_name is the booking contact, NOT automatically an attendee
  const guestList = useMemo(() => {
    // Helper function to get payment share status for a guest (by email match)
    const getPaymentShareStatus = (guestEmail, bookingId) => {
      const bookingShares = paymentShares[bookingId] || [];
      if (!guestEmail || !bookingShares.length) return 'none';
      
      const matchingShare = bookingShares.find(
        s => s.dancer_email?.toLowerCase() === guestEmail.toLowerCase()
      );
      
      return matchingShare?.status || 'none';
    };

    // Helper function to check if booking has any payment shares
    const bookingHasShares = (bookingId) => {
      const shares = paymentShares[bookingId] || [];
      return shares.length > 0;
    };

    const attendees = [];
    
    activeBookings.forEach(booking => {
      const contactName = booking.customer_name || 'Unknown';
      const contactEmail = (booking.customer_email || '').toLowerCase();
      const bookingGuests = booking.guests || [];
      const numberOfGuests = booking.number_of_guests || 1;
      const roomsArray = Array.isArray(booking.rooms) ? booking.rooms : [];
      const roomType = roomsArray.find(r => r.quantity > 0)?.room_type || null;
      const addOns = (booking.selected_add_ons || []).map(a => a.name);
      const specialRequests = booking.special_requests || null;
      
      // Count named attendees in guests[] array
      const namedAttendees = bookingGuests.filter(g => {
        const name = (g.name || `${g.first_name || ''} ${g.last_name || ''}`.trim());
        return name && name.trim().length > 0;
      });
      
      const namedCount = namedAttendees.length;
      const outstandingCount = Math.max(0, numberOfGuests - namedCount);
      
      // Create summary info for the booking
      const attendeeSummary = outstandingCount > 0 
        ? `${namedCount} named · ${outstandingCount} names outstanding`
        : `${namedCount} attendee${namedCount !== 1 ? 's' : ''}`;
      
      // Check if booking contact appears in guests[] (by name or email)
      const contactIsAttending = bookingGuests.some(g => {
        const gName = (g.name || `${g.first_name || ''} ${g.last_name || ''}`.trim()).toLowerCase();
        const gEmail = (g.email || '').toLowerCase();
        return (gName && gName === contactName.toLowerCase()) || 
               (contactEmail && gEmail && gEmail === contactEmail);
      });
      
      // Build attendee rows from guests[] array ONLY
      bookingGuests.forEach((g, idx) => {
        const attendeeName = g.name || `${g.first_name || ''} ${g.last_name || ''}`.trim();
        if (!attendeeName) return; // Skip entries without names
        
        const attendeeEmail = (g.email || '').toLowerCase();
        
        // Check if THIS attendee is the booking contact
        const isBookingContact = 
          (attendeeName.toLowerCase() === contactName.toLowerCase()) ||
          (contactEmail && attendeeEmail && attendeeEmail === contactEmail);
        
        attendees.push({
          id: `${booking.booking_id}-attendee-${idx}`,
          name: attendeeName,
          email: g.email || null,
          isBookingContact: isBookingContact,
          bookingId: booking.booking_id,
          contactName: contactName,
          contactEmail: booking.customer_email,
          bookingStatus: booking.status,
          roomType,
          addOns,
          specialRequests,
          booking,
          attendeeSummary,
          outstandingCount,
          totalGuests: numberOfGuests,
          namedCount,
          contactIsAttending,
          paymentShareStatus: getPaymentShareStatus(g.email, booking.booking_id),
          hasPaymentShares: bookingHasShares(booking.booking_id),
        });
      });
    });
    
    return attendees;
  }, [activeBookings, paymentShares]);

  // Calculate stats - correct model: guests[] is authoritative for attendee names
  const stats = useMemo(() => {
    // Total authoritative attendee count from all active bookings
    const authoritativeTotal = activeBookings.reduce(
      (sum, b) => sum + (b.number_of_guests || 1), 0
    );
    
    // Named attendees = length of guestList (built from guests[] only)
    const namedAttendees = guestList.length;
    const outstandingNames = Math.max(0, authoritativeTotal - namedAttendees);
    
    // Count attendees who are also the booking contact
    const bookingContactsAttending = guestList.filter(g => g.isBookingContact).length;
    
    // Count bookings with special requests
    const bookingsWithRequests = new Set(
      guestList.filter(g => g.specialRequests).map(g => g.bookingId)
    ).size;
    
    // Also count bookings with no named attendees but have special requests
    const bookingsWithRequestsNoAttendees = activeBookings.filter(b => 
      b.special_requests && (!b.guests || b.guests.length === 0)
    ).length;
    
    const totalBookingsWithRequests = bookingsWithRequests + bookingsWithRequestsNoAttendees;
    
    // Count attendees with payment shares (by email match)
    const withPaymentShares = guestList.filter(
      g => g.paymentShareStatus && g.paymentShareStatus !== 'none'
    ).length;
    
    // Count bookings that have any payment shares
    const bookingsWithShares = new Set(
      Object.keys(paymentShares).filter(k => paymentShares[k]?.length > 0)
    ).size;
    
    return {
      authoritativeTotal,
      namedAttendees,
      outstandingNames,
      bookingContactsAttending,
      bookingsWithRequests: totalBookingsWithRequests,
      withPaymentShares,
      bookingsWithShares,
    };
  }, [guestList, activeBookings, paymentShares]);

  // Filter and sort attendees
  const filteredGuests = useMemo(() => {
    let filtered = [...guestList];
    
    // Search - includes searching by booking contact name
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(g => 
        (g.name || '').toLowerCase().includes(query) ||
        (g.email || '').toLowerCase().includes(query) ||
        (g.contactName || '').toLowerCase().includes(query) ||
        (g.bookingId || '').toLowerCase().includes(query)
      );
    }
    
    // Filter
    switch (guestFilter) {
      case 'contact-attending':
        filtered = filtered.filter(g => g.isBookingContact);
        break;
      case 'requests':
        filtered = filtered.filter(g => g.specialRequests);
        break;
      case 'shares':
        filtered = filtered.filter(g => g.hasPaymentShares);
        break;
      default:
        break;
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name':
          aVal = (a.name || 'ZZZ').toLowerCase();
          bVal = (b.name || 'ZZZ').toLowerCase();
          break;
        case 'booking':
          aVal = a.contactName.toLowerCase();
          bVal = b.contactName.toLowerCase();
          break;
        case 'room':
          aVal = a.roomType || 'ZZZ';
          bVal = b.roomType || 'ZZZ';
          break;
        default:
          aVal = a.name || '';
          bVal = b.name || '';
      }
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
    
    return filtered;
  }, [guestList, searchQuery, guestFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-3 h-3 ml-1" /> : 
      <ChevronDown className="w-3 h-3 ml-1" />;
  };

  const openGuestDetail = (guest) => {
    setSelectedGuest(guest);
    setDetailOpen(true);
  };

  const handleViewBooking = (booking) => {
    setDetailOpen(false);
    if (onBookingSelect) onBookingSelect(booking);
  };

  const openShareDialog = (booking) => {
    setDetailOpen(false);
    setShareDialogBooking(booking);
    setShareDialogOpen(true);
  };

  const handleShareUpdate = () => {
    fetchPaymentShares();
    if (onRefresh) onRefresh();
  };

  const isLoading = loading;

  // Determine available filters based on data - use reliable concepts only
  const availableFilters = useMemo(() => {
    const filters = [
      { value: 'all', label: 'All Attendees' },
    ];
    
    // Only show "Booking Contact is Attending" if there are any
    if (stats.bookingContactsAttending > 0) {
      filters.push({ value: 'contact-attending', label: 'Booking Contact is Attending' });
    }
    
    // Only show Special Requests filter if there are any
    if (stats.bookingsWithRequests > 0) {
      filters.push({ value: 'requests', label: 'Special Requests' });
    }
    
    // Only show Payment Share filter if there are any shares
    if (stats.bookingsWithShares > 0) {
      filters.push({ value: 'shares', label: 'Payment Share' });
    }
    
    return filters;
  }, [stats]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Guests</h2>
          <p className="text-sm text-slate-500">All attendees for this event</p>
        </div>
        <div className="flex items-center gap-2">
          <GuestListCSVExport 
            guestList={guestList.map(g => ({
              ...g,
              dietary: g.specialRequests || ''  // Map to expected field for CSV
            }))}
            eventSlug={event?.slug}
            disabled={isLoading || guestList.length === 0}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={isLoading}
            className="text-slate-500 hover:text-slate-700"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1.5" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="Total Attendees"
          value={stats.authoritativeTotal}
          subtitle={stats.outstandingNames > 0 
            ? `${stats.namedAttendees} named · ${stats.outstandingNames} names outstanding` 
            : `${stats.namedAttendees} named`}
          icon={Users}
        />
        <StatCard
          title="Named Attendees"
          value={stats.namedAttendees}
          subtitle="in directory"
          icon={UserCheck}
        />
        <StatCard
          title="Booking Contacts Attending"
          value={stats.bookingContactsAttending}
          subtitle="contacts in guests[]"
          icon={User}
          variant="muted"
        />
        <StatCard
          title="Special Requests"
          value={stats.bookingsWithRequests}
          subtitle="bookings"
          icon={MessageSquare}
          variant={stats.bookingsWithRequests > 0 ? 'warning' : 'default'}
        />
      </div>

      {/* Data Quality Notice - Missing names */}
      {stats.outstandingNames > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Missing Attendee Names</p>
            <p className="mt-1 text-blue-700">
              {stats.outstandingNames} of {stats.authoritativeTotal} attendees do not have names recorded in the booking system. 
              The directory below shows the {stats.namedAttendees} attendees with names available.
            </p>
          </div>
        </div>
      )}

      {/* Attendee Directory */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-violet-600" />
            Attendee Directory
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search guest name, lead booker, email, or booking ref..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="guest-search-input"
              />
              {searchQuery && (
                <button
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Select value={guestFilter} onValueChange={setGuestFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="guest-filter-select">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                {availableFilters.map(f => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Count */}
          <div className="text-sm text-slate-500 mb-3">
            {filteredGuests.length} of {guestList.length} named attendees
            {guestFilter !== 'all' && (
              <span className="ml-2 text-violet-600">
                (filtered: {availableFilters.find(f => f.value === guestFilter)?.label})
              </span>
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : guestList.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No attendees with names recorded</p>
              <p className="text-sm mt-1">Attendee names will appear here as bookings are completed</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm" data-testid="guest-directory-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th 
                        className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('name')}
                      >
                        <span className="flex items-center">ATTENDEE <SortIcon field="name" /></span>
                      </th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">BOOKING CONTACT</th>
                      <th 
                        className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('booking')}
                      >
                        <span className="flex items-center">BOOKING REF <SortIcon field="booking" /></span>
                      </th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">EMAIL</th>
                      <th 
                        className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('room')}
                      >
                        <span className="flex items-center">ROOM <SortIcon field="room" /></span>
                      </th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">ADD-ONS</th>
                      <th className="text-center py-2 px-3 font-medium text-slate-600">REQUESTS</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-600">PAYMENT SHARE</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-600">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGuests.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8 text-slate-500">
                          No attendees match your search or filter
                        </td>
                      </tr>
                    ) : filteredGuests.map((guest) => (
                      <tr 
                        key={guest.id} 
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                        onClick={() => openGuestDetail(guest)}
                        data-testid={`guest-row-${guest.id}`}
                      >
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            {guest.isBookingContact ? (
                              <UserCheck className="w-4 h-4 text-violet-600 flex-shrink-0" />
                            ) : (
                              <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            )}
                            <span className="font-medium text-slate-900">{guest.name}</span>
                            {guest.isBookingContact && (
                              <Badge className="bg-violet-100 text-violet-700 text-xs">Contact</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-slate-600">
                          {guest.isBookingContact ? '-' : guest.contactName}
                        </td>
                        <td className="py-3 px-3">
                          <span 
                            className="font-mono text-xs text-violet-600 hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              onBookingSelect(guest.booking);
                            }}
                          >
                            {guest.bookingId.length > 15 ? `${guest.bookingId.slice(0, 15)}...` : guest.bookingId}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-slate-600">
                          {guest.email ? (
                            <span className="text-sm">{guest.email}</span>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          {guest.roomType ? (
                            <span className="text-sm text-slate-700">{guest.roomType}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          {guest.addOns && guest.addOns.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {guest.addOns.slice(0, 2).map((addon, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs text-violet-600 border-violet-200">
                                  {addon}
                                </Badge>
                              ))}
                              {guest.addOns.length > 2 && (
                                <Badge variant="outline" className="text-xs text-slate-500">
                                  +{guest.addOns.length - 2}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center">
                          {guest.specialRequests ? (
                            <div className="flex justify-center">
                              <MessageSquare 
                                className="w-4 h-4 text-orange-500" 
                                title={guest.specialRequests}
                              />
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-3" onClick={(e) => e.stopPropagation()}>
                          {guest.paymentShareStatus && guest.paymentShareStatus !== 'none' ? (
                            <PaymentShareStatusBadge 
                              status={guest.paymentShareStatus} 
                              onClick={() => openShareDialog(guest.booking)}
                            />
                          ) : guest.hasPaymentShares ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs text-violet-600 h-7 px-2"
                              onClick={() => openShareDialog(guest.booking)}
                            >
                              <CreditCard className="w-3 h-3 mr-1" />
                              Manage
                            </Button>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openGuestDetail(guest)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards - Prioritized columns */}
              <div className="md:hidden space-y-3" data-testid="guest-directory-mobile">
                {filteredGuests.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No attendees match your search or filter
                  </div>
                ) : filteredGuests.map((guest) => (
                  <div 
                    key={guest.id}
                    className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 cursor-pointer"
                    onClick={() => openGuestDetail(guest)}
                    data-testid={`guest-card-${guest.id}`}
                  >
                    {/* Primary info: Attendee name and type */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {guest.isBookingContact ? (
                          <UserCheck className="w-4 h-4 text-violet-600" />
                        ) : (
                          <User className="w-4 h-4 text-slate-400" />
                        )}
                        <span className="font-medium text-slate-900">{guest.name}</span>
                      </div>
                      {guest.isBookingContact && (
                        <Badge className="bg-violet-100 text-violet-700 text-xs">Contact</Badge>
                      )}
                    </div>
                    
                    {/* Secondary info: Booking and Room */}
                    <div className="text-xs text-slate-500 space-y-1 mb-2">
                      <p className="flex items-center gap-2">
                        <span className="font-mono text-violet-600">{guest.bookingId.slice(0, 12)}...</span>
                        {!guest.isBookingContact && (
                          <span className="text-slate-400">booked by {guest.contactName}</span>
                        )}
                      </p>
                      {guest.roomType && (
                        <p className="flex items-center gap-1">
                          <Hotel className="w-3 h-3" />
                          {guest.roomType}
                        </p>
                      )}
                    </div>
                    
                    {/* Status indicators */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {guest.specialRequests && (
                        <Badge variant="outline" className="text-orange-600 border-orange-200 text-xs">
                          <MessageSquare className="w-3 h-3 mr-1" />
                          Request
                        </Badge>
                      )}
                      {guest.paymentShareStatus && guest.paymentShareStatus !== 'none' && (
                        <PaymentShareStatusBadge 
                          status={guest.paymentShareStatus}
                          onClick={(e) => {
                            e.stopPropagation();
                            openShareDialog(guest.booking);
                          }}
                        />
                      )}
                      {guest.addOns && guest.addOns.length > 0 && (
                        <Badge variant="outline" className="text-xs text-slate-500">
                          {guest.addOns.length} add-on{guest.addOns.length > 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Guest Detail Sheet */}
      <GuestDetailSheet
        guest={selectedGuest}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onViewBooking={handleViewBooking}
        onManageShare={openShareDialog}
        authToken={authToken}
      />

      {/* Payment Share Manager Dialog */}
      <PaymentShareDialog
        booking={shareDialogBooking}
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        authToken={authToken}
        onUpdate={handleShareUpdate}
      />
    </div>
  );
}
